import { FileText, X, Loader2, AlertTriangle } from "lucide-react";
import { computeLinePrice } from "@/lib/pricing";
import { InfoTip } from "@/components/InfoTip";

const GROUPS = {
  color: [{ v: "bw", label: "Mono" }, { v: "color", label: "Colour" }],
  size: [{ v: "A4", label: "A4" }, { v: "A3", label: "A3" }],
  paper: [{ v: "80", label: "80gsm" }, { v: "250", label: "250gsm" }],
  sided: [{ v: "single", label: "Single" }, { v: "double", label: "Double" }],
};

const TIPS = {
  copies: "The number of finished copies you need of this document.",
  colour: "Mono prints in black & white only. Colour prints in full colour and costs more per page.",
  size: "A4 is standard document size (210×297mm). A3 is twice as large — poster size.",
  paper: "80gsm is standard printer paper. 250gsm is thick, rigid card stock.",
  sides: "Single-sided prints on one side of each sheet. Double-sided prints on both sides — each printed side counts as a page.",
};

function Seg({ name, options, value, onChange, docId }) {
  return (
    <div className="flex rounded-lg border-2 border-slate-200 overflow-hidden w-fit">
      {options.map((o) => (
        <button
          key={o.v}
          type="button"
          data-testid={`doc-${docId}-${name}-${o.v}`}
          onClick={() => onChange(o.v)}
          className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
            value === o.v ? "bg-brand text-white" : "bg-white text-slate-600 hover:bg-slate-50"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Field({ label, tip, children }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="uppercase text-[10px] tracking-[0.14em] font-semibold text-slate-500">{label}</span>
        <InfoTip text={tip} label={label.toLowerCase()} />
      </div>
      {children}
    </div>
  );
}

export function DocumentCard({ doc, pricing, currency, onChange, onRemove }) {
  const set = (k, v) => onChange({ ...doc, opts: { ...doc.opts, [k]: v } });
  const price = computeLinePrice(pricing, doc.opts, doc.pages || 1);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 animate-fade-up" data-testid={`doc-card-${doc.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="grid place-items-center w-10 h-10 rounded-lg bg-orange-50 text-brand shrink-0"><FileText size={18} /></span>
          <div className="min-w-0">
            <div className="font-semibold text-ink truncate">{doc.file.name}</div>
            <div className="text-xs text-slate-500 mt-0.5">
              {doc.counting ? (
                <span className="inline-flex items-center gap-1"><Loader2 className="animate-spin" size={11} /> Counting pages…</span>
              ) : doc.pagesKnown ? (
                <span data-testid={`doc-pages-${doc.id}`}>{doc.pages} {doc.pages > 1 ? "pages" : "page"}</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-amber-600" data-testid={`doc-unknown-${doc.id}`}><AlertTriangle size={11} /> Page count unavailable — priced as 1 page</span>
              )}
            </div>
          </div>
        </div>
        {onRemove && (
          <button type="button" onClick={onRemove} data-testid={`doc-remove-${doc.id}`} className="text-slate-400 hover:text-red-500 shrink-0 transition-colors"><X size={18} /></button>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-5">
        <Field label="Copies" tip={TIPS.copies}>
          <input
            type="number" min={1} value={doc.opts.copies} data-testid={`doc-${doc.id}-copies`}
            onChange={(e) => { const v = e.target.value; set("copies", v === "" ? "" : Math.max(1, parseInt(v, 10) || 1)); }}
            onBlur={(e) => { if (e.target.value === "" || parseInt(e.target.value, 10) < 1) set("copies", 1); }}
            className="w-16 px-2 py-1.5 rounded-md border-2 border-slate-200 focus:border-brand focus:outline-none text-ink font-medium"
          />
        </Field>
        <Field label="Colour" tip={TIPS.colour}><Seg name="color" docId={doc.id} options={GROUPS.color} value={doc.opts.color} onChange={(v) => set("color", v)} /></Field>
        <Field label="Size" tip={TIPS.size}><Seg name="size" docId={doc.id} options={GROUPS.size} value={doc.opts.size} onChange={(v) => set("size", v)} /></Field>
        <Field label="Paper" tip={TIPS.paper}><Seg name="paper" docId={doc.id} options={GROUPS.paper} value={doc.opts.paper} onChange={(v) => set("paper", v)} /></Field>
        <Field label="Sides" tip={TIPS.sides}><Seg name="sided" docId={doc.id} options={GROUPS.sided} value={doc.opts.sided} onChange={(v) => set("sided", v)} /></Field>
      </div>

      <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-slate-100">
        <span className="text-[10px] uppercase tracking-widest text-slate-400">Subtotal</span>
        <span className="font-heading font-extrabold text-lg text-ink" data-testid={`doc-price-${doc.id}`}>{currency}{price.toFixed(2)}</span>
      </div>
    </div>
  );
}
